import { create } from 'zustand';
import * as vibecoinAPI from '@/api/vibecoin';
import type {
  VibeCoinBalance,
  VibeCoinTransaction,
  VibeCoinShopItem,
  VibeCoinActiveEffect,
  VibeCoinEvent,
} from '@/api/vibecoin';

interface VibeCoinState {
  // State
  balance: number;
  recentTransactions: VibeCoinTransaction[];
  history: VibeCoinTransaction[];
  historyPage: number;
  historyHasMore: boolean;
  historyFilter: 'all' | 'credit' | 'debit';
  shop: VibeCoinShopItem[];
  activeEffects: VibeCoinActiveEffect[];
  activeEvents: VibeCoinEvent[];
  loading: boolean;
  shopLoading: boolean;
  error: string | null;

  // Actions
  fetchBalance: () => Promise<void>;
  fetchHistory: (page?: number) => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  setHistoryFilter: (filter: 'all' | 'credit' | 'debit') => void;
  fetchShop: () => Promise<void>;
  purchaseItem: (itemId: string) => Promise<boolean>;
  fetchActiveEffects: () => Promise<void>;
  fetchActiveEvents: () => Promise<void>;
  optimisticCredit: (amount: number, source: string, description: string) => void;
  reset: () => void;
}

export const useVibeCoinStore = create<VibeCoinState>((set, get) => ({
  balance: 0,
  recentTransactions: [],
  history: [],
  historyPage: 1,
  historyHasMore: false,
  historyFilter: 'all',
  shop: [],
  activeEffects: [],
  activeEvents: [],
  loading: false,
  shopLoading: false,
  error: null,

  fetchBalance: async () => {
    try {
      const data = await vibecoinAPI.getBalance();
      set({
        balance: data.balance,
        recentTransactions: data.recentTransactions,
        error: null,
      });
    } catch (err: any) {
      // Silent fail — balance shows 0
      console.error('[VibeCoin] fetchBalance error:', err);
    }
  },

  fetchHistory: async (page = 1) => {
    set({ loading: true });
    try {
      const { historyFilter } = get();
      const direction = historyFilter === 'all' ? undefined : historyFilter;
      const data = await vibecoinAPI.getHistory(page, direction);
      set({
        history: page === 1 ? data.transactions : [...get().history, ...data.transactions],
        historyPage: data.pagination.page,
        historyHasMore: data.pagination.hasMore,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      set({ loading: false, error: err.message });
    }
  },

  loadMoreHistory: async () => {
    const { historyPage, historyHasMore, loading } = get();
    if (!historyHasMore || loading) return;
    await get().fetchHistory(historyPage + 1);
  },

  setHistoryFilter: (filter) => {
    set({ historyFilter: filter, history: [], historyPage: 1, historyHasMore: false });
    get().fetchHistory(1);
  },

  fetchShop: async () => {
    set({ shopLoading: true });
    try {
      const items = await vibecoinAPI.getShop();
      set({ shop: items, shopLoading: false, error: null });
    } catch (err: any) {
      set({ shopLoading: false, error: err.message });
    }
  },

  purchaseItem: async (itemId: string) => {
    const idempotencyKey = `${itemId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      const result = await vibecoinAPI.purchaseItem(itemId, idempotencyKey);
      set({ balance: result.newBalance });
      // Refresh shop to update canBuy flags
      get().fetchShop();
      get().fetchActiveEffects();
      return true;
    } catch (err: any) {
      set({ error: err.message });
      return false;
    }
  },

  fetchActiveEffects: async () => {
    try {
      const effects = await vibecoinAPI.getActiveEffects();
      set({ activeEffects: effects });
    } catch (err: any) {
      console.error('[VibeCoin] fetchActiveEffects error:', err);
    }
  },

  fetchActiveEvents: async () => {
    try {
      const events = await vibecoinAPI.getActiveEvents();
      set({ activeEvents: events });
    } catch (err: any) {
      console.error('[VibeCoin] fetchActiveEvents error:', err);
    }
  },

  optimisticCredit: (amount, source, description) => {
    const { balance, recentTransactions } = get();
    const newBalance = balance + amount;
    set({
      balance: newBalance,
      recentTransactions: [
        {
          id: `optimistic-${Date.now()}`,
          type: 'credit',
          amount,
          source,
          description,
          balance_after: newBalance,
          created_at: Date.now(),
        },
        ...recentTransactions.slice(0, 4),
      ],
    });
  },

  reset: () => set({
    balance: 0,
    recentTransactions: [],
    history: [],
    historyPage: 1,
    historyHasMore: false,
    historyFilter: 'all',
    shop: [],
    activeEffects: [],
    activeEvents: [],
    loading: false,
    shopLoading: false,
    error: null,
  }),
}));
